import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Modal, message } from 'antd';
import { ncaLayerService } from '../../services/ncaLayerService';
import './AdminOrderSignatures.css';

interface OrderSignature {
  id: number;
  orderId: string;
  orderGuid: string;
  status: string;
  companyBin: string;
  companyName: string;
  isSignedByBuyer: boolean;
  isSignedBySupplier: boolean;
  buyerSignedAt: string | null;
  supplierSignedAt: string | null;
  generatedPdfPath: string;
  createdAt: string;
}

const AdminOrderSignatures: React.FC = () => {
  const navigate = useNavigate();
  const [orderSignatures, setOrderSignatures] = useState<OrderSignature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ncaLayerStatus, setNcaLayerStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');
  const [selectedCertificate, setSelectedCertificate] = useState<string>('PKCS12');
  const [certificates, setCertificates] = useState<any[]>([]);

  useEffect(() => {
    loadOrderSignatures();
    checkNCALayer();
  }, []);

  const checkNCALayer = async () => {
    try {
      // Запускаем NCALayer WebSocket
      ncaLayerService.start();
      
      // Проверяем доступность
      const isConnected = await ncaLayerService.isAvailable();
      if (isConnected) {
        setNcaLayerStatus('available');
        // Получаем сертификаты
        const certs = await ncaLayerService.getCertificates();
        setCertificates(certs);
      } else {
        setNcaLayerStatus('unavailable');
      }
    } catch (error) {
      console.error('Ошибка проверки NCALayer:', error);
      setNcaLayerStatus('unavailable');
    }
  };

  const loadOrderSignatures = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('adminToken') || localStorage.getItem('accessToken');
      if (!token) {
        navigate('/admin');
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/order-signatures`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setOrderSignatures(response.data.data);
      } else {
        throw new Error(response.data.message || 'Не удалось загрузить заявки');
      }
    } catch (err: any) {
      console.error('Ошибка загрузки заявок:', err);
      setError(err.response?.data?.message || err.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleSignBySupplier = async (orderSignatureId: number, orderSignature: OrderSignature) => {
    // Проверяем доступность NCA Layer
    if (ncaLayerStatus === 'checking') {
      message.warning('Проверка доступности NCALayer...');
      return;
    }

    if (ncaLayerStatus === 'unavailable') {
      Modal.error({
        title: 'NCALayer недоступен',
        content: 'Для подписания документов необходимо установить и запустить NCALayer. После установки перезагрузите страницу.',
        okText: 'Понятно'
      });
      return;
    }

    Modal.confirm({
      title: 'Подписать заявку через NCALayer',
      content: (
        <div>
          <p>Вы действительно хотите подписать эту заявку от имени поставщика?</p>
          <p style={{ marginTop: '10px', color: '#666', fontSize: '12px' }}>
            Заявка: <strong>{orderSignature.orderId}</strong><br />
            Компания: <strong>{orderSignature.companyName}</strong>
          </p>
        </div>
      ),
      okText: 'Подписать',
      cancelText: 'Отмена',
      onOk: async () => {
        const hideLoading = message.loading('Подписание через NCALayer...', 0);
        
        try {
          const token = localStorage.getItem('adminToken') || localStorage.getItem('accessToken');
          if (!token) {
            hideLoading();
            navigate('/admin');
            return;
          }

          // Используем выбранный сертификат или PKCS12 по умолчанию
          const certPath = selectedCertificate || 'PKCS12';

          // Подготавливаем данные для подписания
          const orderData = JSON.stringify({
            orderSignatureId: orderSignatureId,
            orderId: orderSignature.orderId,
            companyBin: orderSignature.companyBin,
            companyName: orderSignature.companyName,
            timestamp: new Date().toISOString()
          });

          // Подписываем данные через NCA Layer
          console.log('🔐 Начало подписания через NCALayer...');
          const signatureResult = await ncaLayerService.signData(orderData, certPath);
          console.log('✅ Подпись получена от NCALayer:', signatureResult);

          // Проверяем, что подпись получена
          if (!signatureResult || !signatureResult.signature || signatureResult.signature.trim() === '') {
            console.error('❌ Подпись не получена:', signatureResult);
            throw new Error('Не удалось получить подпись от NCA Layer. Проверьте подключение и сертификат.');
          }

          // Отправляем подпись на бэкенд
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/admin/order-signatures/${orderSignatureId}/sign`,
            {
              signatureData: signatureResult.signature,
              certificateData: signatureResult.certificate,
              signerName: signatureResult.signerInfo.commonName,
              organizationName: signatureResult.signerInfo.organization,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          hideLoading();

          if (response.data.success) {
            message.success('Заявка успешно подписана от имени поставщика');
            loadOrderSignatures();
          } else {
            throw new Error(response.data.message || 'Ошибка подписания');
          }
        } catch (err: any) {
          hideLoading();
          console.error('❌ Ошибка подписания:', err);
          
          // Более подробное сообщение об ошибке
          let errorMessage = 'Не удалось подписать заявку';
          if (err.message) {
            errorMessage = err.message;
          } else if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          }
          
          // Специальные сообщения для типичных ошибок
          if (errorMessage.includes('отменено') || errorMessage.includes('canceled')) {
            errorMessage = 'Подписание отменено пользователем';
          } else if (errorMessage.includes('NCALayer') || errorMessage.includes('connection')) {
            errorMessage = 'Ошибка связи с NCALayer. Убедитесь что он запущен.';
          } else if (errorMessage.includes('certificate') || errorMessage.includes('сертификат')) {
            errorMessage = 'Ошибка с сертификатом. Проверьте срок действия.';
          }
          
          message.error(errorMessage, 5);
        }
      },
    });
  };

  const handleDownloadPdf = async (orderSignatureId: number, orderId: string) => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('accessToken');
      if (!token) {
        navigate('/admin');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/order-signatures/${orderSignatureId}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `order-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Ошибка скачивания PDF:', err);
      message.error('Не удалось скачать PDF');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (orderSignature: OrderSignature) => {
    if (orderSignature.isSignedByBuyer && orderSignature.isSignedBySupplier) {
      return <span className="status-badge status-signed-both">Подписана обеими сторонами</span>;
    } else if (orderSignature.isSignedByBuyer) {
      return <span className="status-badge status-signed-buyer">Подписана покупателем</span>;
    } else {
      return <span className="status-badge status-pending">Ожидает подписания</span>;
    }
  };

  if (loading) {
    return (
      <div className="admin-order-signatures">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Загрузка заявок...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-order-signatures">
        <div className="error-message">
          <h3>Ошибка</h3>
          <p>{error}</p>
          <button onClick={loadOrderSignatures} className="btn-retry">
            Повторить попытку
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-order-signatures">
      <div className="page-header">
        <h1>Заявки на поставку</h1>
        <button onClick={loadOrderSignatures} className="btn-refresh">
          🔄 Обновить
        </button>
      </div>

      {orderSignatures.length === 0 ? (
        <div className="no-data">
          <p>Нет заявок</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="order-signatures-table">
            <thead>
              <tr>
                <th>ID заявки</th>
                <th>Компания</th>
                <th>БИН</th>
                <th>Статус</th>
                <th>Подписана покупателем</th>
                <th>Подписана поставщиком</th>
                <th>Дата создания</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {orderSignatures.map((os) => (
                <tr key={os.id}>
                  <td className="order-id">{os.orderId}</td>
                  <td>{os.companyName || '-'}</td>
                  <td>{os.companyBin || '-'}</td>
                  <td>{getStatusBadge(os)}</td>
                  <td className="text-center">
                    {os.isSignedByBuyer ? (
                      <span className="icon-check">✅</span>
                    ) : (
                      <span className="icon-cross">❌</span>
                    )}
                    {os.buyerSignedAt && <div className="date-small">{formatDate(os.buyerSignedAt)}</div>}
                  </td>
                  <td className="text-center">
                    {os.isSignedBySupplier ? (
                      <span className="icon-check">✅</span>
                    ) : (
                      <span className="icon-cross">❌</span>
                    )}
                    {os.supplierSignedAt && <div className="date-small">{formatDate(os.supplierSignedAt)}</div>}
                  </td>
                  <td>{formatDate(os.createdAt)}</td>
                  <td className="actions-cell">
                    <button
                      onClick={() => handleDownloadPdf(os.id, os.orderId)}
                      className="btn-download"
                      title="Скачать PDF"
                    >
                      📥 PDF
                    </button>
                    {os.isSignedByBuyer && !os.isSignedBySupplier && (
                      <button
                        onClick={() => handleSignBySupplier(os.id, os)}
                        className="btn-sign"
                        title="Подписать от имени поставщика через NCALayer"
                      >
                        ✍️ Подписать
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminOrderSignatures;

